import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import AuthService from "../../../api/services/AuthService";
import { matchesContractSymbol, rowBetweenDateInputs, settlementMatchesContractCode, spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import SpotOrdersExportModal from "./SpotOrdersExportModal";
import OrderHistoryDownloadButtons from "./OrderHistoryDownloadButtons";
import { hideThenShowExport, showExportModal } from "./orderExportHelpers";
import { EXPORT_KIND_FUTURES_POSITIONS } from "./ordersHistoryExport";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const EXPORT_MODAL_ID = "exportPositionOrdersModal";
const MOB_DL_SEL = "#mob-position-orders-filters-dl";

const ORDER_DESKTOP_COL_COUNT = 9;

const SETTLEMENT_OPTIONS = [
  { value: "usdt", label: "USDT" },
  { value: "usdc", label: "USDC" },
];

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btcusdt", label: "BTCUSDT" },
  { value: "ethusdt", label: "ETHUSDT" },
  { value: "bnbusdt", label: "BNBUSDT" },
];

function formatPosTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

function decStr(v) {
  if (v == null) return "—";
  if (typeof v === "object" && v.$numberDecimal != null) return String(v.$numberDecimal);
  const n = parseFloat(v);
  return Number.isFinite(n) ? String(n) : String(v);
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const PositionOrders = () => {
  const initRange = spotActivityDefaultLocalRange();
  const [settlement, setSettlement] = useState("usdt");
  const [contract, setContract] = useState("all");
  const [contract2, setContract2] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [exportPreset, setExportPreset] = useState("custom");

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const loadRows = useCallback(async () => {
    try {
      const res = await AuthService.futuresPositionHistory({
        from: dateFrom ? `${dateFrom}T00:00:00.000Z` : undefined,
        to: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
        skip: 0,
        limit: 100,
      });
      if (!res?.success) {
        setRows([]);
        return;
      }
      const items = res?.data?.positions ?? [];
      const mapped = (Array.isArray(items) ? items : []).map((r, idx) => {
        const pnl = decStr(r.realized_pnl);
        const pnlPct = decStr(r.realized_pnl_pct);
        return {
          id: r._id ?? r.id ?? idx,
          contract: String(r.symbol ?? "—").toUpperCase(),
          side: String(r.side ?? "—"),
          status: String(r.status ?? "—"),
          entryTime: formatPosTime(r.opened_at ?? r.created_at),
          entryPrice: decStr(r.entry_price ?? r.avg_entry_price),
          avgExitPrice: decStr(r.exit_price ?? r.avg_exit_price),
          cumulativeClosed: decStr(r.closed_quantity ?? r.closed_qty),
          peakPosition: decStr(r.max_quantity ?? r.peak_quantity),
          realizedReturn: pnl !== "—" && !String(pnl).startsWith("-") ? `+${pnl}` : pnl,
          realizedPct: pnlPct !== "—" ? `(${pnlPct}%)` : "—",
        };
      });
      setRows(mapped);
    } catch {
      setRows([]);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!settlementMatchesContractCode(row.contract, settlement)) return false;
      if (!matchesContractSymbol(row.contract, contract)) return false;
      if (contract2 !== "all" && !matchesContractSymbol(row.contract, contract2)) return false;
      if (!rowBetweenDateInputs(row.entryTime, dateFrom, dateTo)) return false;
      return true;
    });
  }, [rows, settlement, contract, contract2, dateFrom, dateTo]);

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setSettlement("usdt");
    setContract("all");
    setContract2("all");
    setDateFrom(from);
    setDateTo(to);
  };

  const openDownload = () => {
    setExportPreset("custom");
    showExportModal(EXPORT_MODAL_ID);
  };

  const openDownloadFromMobile = () => {
    setExportPreset("custom");
    hideThenShowExport(MOB_DL_SEL, EXPORT_MODAL_ID);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter position orders">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}po-dd-settlement`}
          label="Settlement Coin"
          value={settlement}
          options={SETTLEMENT_OPTIONS}
          onChange={setSettlement}
          menuKey={`${pref}po-settlement`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}po-dd-contract`}
          label="Contract"
          value={contract}
          options={CONTRACT_OPTIONS}
          onChange={setContract}
          menuKey={`${pref}po-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}po-dd-contract2`}
          label="Contract"
          value={contract2}
          options={CONTRACT_OPTIONS}
          onChange={setContract2}
          menuKey={`${pref}po-contract2`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <div className="so-dateBlock">
          <span className="so-dateBlock__label">Date</span>
          <div className="so-dateRow">
            <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start date" inputRef={fromRef} />
            <span className="so-dateArrow" aria-hidden="true">
              →
            </span>
            <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End date" inputRef={toRef} />
            <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="so-toolbar__right">
        {!stacked ? (
          <OrderHistoryDownloadButtons onDownload={openDownload} />
        ) : null}
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={9} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-position-orders-title">
            <h1 id="so-position-orders-title" className="so-page__title">
              Position Orders
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
                { label: "Order History", to: "/user_profile/orders/futures_order_history" },
                { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
                { label: "Position History", to: "/user_profile/orders/position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap">
              <table className="so-table so-table--air so-table--positions">
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col" className="so-table__th--center">
                      Side
                    </th>
                    <th scope="col" className="so-table__th--center">
                      Status
                    </th>
                    <th scope="col">Entry Time</th>
                    <th scope="col" className="so-table__th--right">
                      Entry Price
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Avg Exit Price
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Cumulative Closed
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Peak Position
                    </th>
                    <th scope="col" className="so-table__th--right">
                      Realized Return/Return %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.contract}</td>
                      <td className="so-table__td--center">
                        <span className="so-side--long">{r.side}</span>
                      </td>
                      <td className="so-table__td--center">
                        <span className="so-badge--closed">{r.status}</span>
                      </td>
                      <td className="so-num">{r.entryTime}</td>
                      <td className="so-table__td--right so-num">{r.entryPrice}</td>
                      <td className="so-table__td--right so-num">{r.avgExitPrice}</td>
                      <td className="so-table__td--right so-num">{r.cumulativeClosed}</td>
                      <td className="so-table__td--right so-num">{r.peakPosition}</td>
                      <td className="so-table__td--right so-num">
                        <span className="so-pnl--profit">{r.realizedReturn}</span> <span className="so-pnl--profit">{r.realizedPct}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Position orders pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Position Orders</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
              { label: "Order History", to: "/user_profile/orders/futures_order_history" },
              { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
              { label: "Position History", to: "/user_profile/orders/position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-position-orders-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
              downloads={
                <OrderHistoryDownloadButtons stacked onDownload={openDownloadFromMobile} />
              }
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((r) => (
            <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Position order details">
              <div className="so-mobileCard__row">
                <span>Contract</span>
                <span>{r.contract}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Side</span>
                <span className="so-side--long">{r.side}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Entry Time</span>
                <span className="so-num">{r.entryTime}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Entry Price</span>
                <span className="so-num">{r.entryPrice}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Avg Exit Price</span>
                <span className="so-num">{r.avgExitPrice}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Cumulative Closed</span>
                <span className="so-num">{r.cumulativeClosed}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Peak Position</span>
                <span className="so-num">{r.peakPosition}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Realized Return/Return %</span>
                <span className="so-pnl--profit so-num">
                  {r.realizedReturn} {r.realizedPct}
                </span>
              </div>
              <div className="so-mobileCard__row">
                <span>Status</span>
                <span className="so-badge--closed">{r.status}</span>
              </div>
            </OrderMobileCardShell>
          ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Position orders pages"
          />
        </div>
      </div>
      <SpotOrdersExportModal
        modalId={EXPORT_MODAL_ID}
        exportKind={EXPORT_KIND_FUTURES_POSITIONS}
        defaultFrom={dateFrom}
        defaultTo={dateTo}
        defaultPreset={exportPreset}
      />
    </div>
  );
};

export default PositionOrders;
